import type { UUID } from '@protector/utils/types';
import { coreServicesApiClient, protectorApiClient, type RequestProps } from '@protector/api-client';
import {
  currentSeasonAreasResponseSchema,
  getPropertiesResponseSchema,
  propertyBasicInfoResponseSchema,
  propertyInfoAddressDTOSchema,
  propertyTimeSchema,
  regionsByPropertyResponseSchema,
  seasonsByPropertyResponseSchema
} from './schemas.ts';

function coreRequestWithDomain<I, O>(args: Omit<RequestProps<I, O>, 'domain'>) {
  return coreServicesApiClient.request<I, O>({ ...args, domain: 'Property' });
}

function protectorRequestWithDomain<I, O>(args: Omit<RequestProps<I, O>, 'domain'>) {
  return protectorApiClient.request<I, O>({ ...args, domain: 'Property' });
}

export function getPropertyData(propertyId: UUID) {
  return coreRequestWithDomain({
    config: {
      method: 'GET',
      url: `/v2/properties/${propertyId}`
    },
    outputSchema: propertyInfoAddressDTOSchema
  });
}

export function getProperties(workspaceId: string, orgIds?: string[]) {
  let url = `/v4/properties?workspace_id=${workspaceId}`;
  if (orgIds) {
    url += `&org_ids[]=${orgIds.join(',')}`;
  }
  return coreRequestWithDomain({
    config: {
      method: 'GET',
      url
    },
    outputSchema: getPropertiesResponseSchema
  });
}

export function getPropertyTime(propertyId: UUID) {
  return coreRequestWithDomain({
    config: {
      method: 'GET',
      url: `/v1/properties/${propertyId}/date-time`
    },
    outputSchema: propertyTimeSchema
  });
}

export function getCurrentSeasonAreas(propertyId: UUID) {
  return coreRequestWithDomain({
    config: {
      method: 'GET',
      url: `/v1/properties/${propertyId}/current-season-areas`
    },
    outputSchema: currentSeasonAreasResponseSchema
  });
}

export function getRegionsByProperty(propertyId: UUID) {
  return protectorRequestWithDomain({
    config: {
      method: 'GET',
      url: `/v1/properties/${propertyId}/regions`
    },
    outputSchema: regionsByPropertyResponseSchema
  });
}

export function getSeasonsByProperty(propertyId: UUID, page = 0, size = 100) {
  return protectorRequestWithDomain({
    config: {
      method: 'GET',
      url: `/v2/properties/${propertyId}/seasons?page=${page}&size=${size}`
    },
    outputSchema: seasonsByPropertyResponseSchema
  });
}

export function getPropertyBasicInfo(includeSeasons = true) {
  return protectorRequestWithDomain({
    config: {
      method: 'GET',
      url: `/v1/panel/properties?includeSeasons=${includeSeasons ? 'yes' : 'no'}&includeAreaInformation=no`
    },
    outputSchema: propertyBasicInfoResponseSchema
  });
}
